import React, { useState, useEffect } from 'react';
import { Chart, registerables } from 'chart.js';
import { useNavigate } from 'react-router-dom';
import { useLocation } from '../../contexts/LocationContext';
import { fetchCES4Data, CES4_INDICATOR_MAP, CES4Data } from '../../services/ces4DataService';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';

Chart.register(...registerables);

interface BurdenBenefitsChartProps {
  type: 'burdens' | 'benefits';
  housingBurdenPercentile?: number | null;
  onPercentileCalculated?: (percentile: number | null) => void;
}

interface Category {
  name: string;
  percentile: number;
  expanded: boolean;
  indicators: {
    name: string;
    percentile: number | null; // null means "coming soon"
  }[];
}

export const BurdenBenefitsChart: React.FC<BurdenBenefitsChartProps> = ({ type, housingBurdenPercentile, onPercentileCalculated }) => {
  const { selectedLocation, selectedGeofips } = useLocation();
  const navigate = useNavigate();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [ces4Data, setCes4Data] = useState<CES4Data | null>(null);
  const [ghgPercentiles, setGhgPercentiles] = useState<{
    ldv?: number;
    hdv?: number;
    electricity?: number;
    naturalGas?: number;
    waste?: number;
    offroad?: number;
    total?: number;
  } | null>(null);
  const [commuteTimePercentile, setCommuteTimePercentile] = useState<number | null>(null);
  const [evPer1000Percentile, setEvPer1000Percentile] = useState<number | null>(null);
  const [evChargingPercentile, setEvChargingPercentile] = useState<number | null>(null);
  const [solarPVPercentile, setSolarPVPercentile] = useState<number | null>(null);
  const [heatPumpPercentile, setHeatPumpPercentile] = useState<number | null>(null);
  const [educationalAttainmentPercentile, setEducationalAttainmentPercentile] = useState<number | null>(null);
  const [healthInsurancePercentile, setHealthInsurancePercentile] = useState<number | null>(null);
  const [equityPercentiles, setEquityPercentiles] = useState<{
    incomeEquity?: number;
    educationEquity?: number;
    wageEquity?: number;
  } | null>(null);

  // Fetch CES4 data when location changes
  useEffect(() => {
    const loadCES4Data = async () => {
      if (!selectedLocation) {
        setCes4Data(null);
        return;
      }

      try {
        const data = await fetchCES4Data(selectedLocation);
        setCes4Data(data);
      } catch (error) {
        console.error('Error loading CES4 data:', error);
        setCes4Data(null);
      }
    };

    loadCES4Data();
  }, [selectedLocation]);

  // Fetch GHG emissions percentiles from ghg_inventory_precalculated
  useEffect(() => {
    const loadGHGPercentiles = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setGhgPercentiles(null);
        return;
      }

      try {
        // Get the latest year's data (typically 2023)
        const { data, error } = await supabaseClient
          .from('ghg_inventory_precalculated')
          .select('year, ldv_emissions_percentile, hdv_emissions_percentile, elec_total_emissions_percentile, ng_total_emissions_percentile, waste_emissions_percentile, offroad_emissions_percentile, total_emissions_percentile')
          .eq('geofips', selectedGeofips)
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading GHG percentiles:', error);
          setGhgPercentiles(null);
          return;
        }

        if (data && data.length > 0) {
          const latest = data[0];
          setGhgPercentiles({
            ldv: latest.ldv_emissions_percentile ? Math.round(latest.ldv_emissions_percentile) : undefined,
            hdv: latest.hdv_emissions_percentile ? Math.round(latest.hdv_emissions_percentile) : undefined,
            electricity: latest.elec_total_emissions_percentile ? Math.round(latest.elec_total_emissions_percentile) : undefined,
            naturalGas: latest.ng_total_emissions_percentile ? Math.round(latest.ng_total_emissions_percentile) : undefined,
            waste: latest.waste_emissions_percentile ? Math.round(latest.waste_emissions_percentile) : undefined,
            offroad: latest.offroad_emissions_percentile ? Math.round(latest.offroad_emissions_percentile) : undefined,
            total: latest.total_emissions_percentile ? Math.round(latest.total_emissions_percentile) : undefined,
          });
        } else {
          setGhgPercentiles(null);
        }
      } catch (error) {
        console.error('Error loading GHG percentiles:', error);
        setGhgPercentiles(null);
      }
    };

    loadGHGPercentiles();
  }, [selectedLocation, selectedGeofips]);

  // Fetch commute time percentile
  useEffect(() => {
    const loadCommuteTimePercentile = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setCommuteTimePercentile(null);
        return;
      }

      try {
        const { data, error } = await supabaseClient
          .from('commute_time')
          .select('year, percentile')
          .eq('geofips', String(selectedGeofips))
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading commute time percentile:', error);
          setCommuteTimePercentile(null);
          return;
        }

        if (data && data.length > 0) {
          setCommuteTimePercentile(data[0].percentile ? Math.round(data[0].percentile) : null);
        } else {
          setCommuteTimePercentile(null);
        }
      } catch (error) {
        console.error('Error loading commute time percentile:', error);
        setCommuteTimePercentile(null);
      }
    };

    loadCommuteTimePercentile();
  }, [selectedLocation, selectedGeofips]);

  // Fetch EV per 1000 people percentile from ghg_inventory_precalculated
  useEffect(() => {
    const loadEVPercentile = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setEvPer1000Percentile(null);
        return;
      }

      try {
        // Get the latest year's data (typically 2023)
        const { data, error } = await supabaseClient
          .from('ghg_inventory_precalculated')
          .select('year, ev_per_1000_people_percentile')
          .eq('geofips', selectedGeofips)
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading EV percentile:', error);
          setEvPer1000Percentile(null);
          return;
        }

        if (data && data.length > 0) {
          setEvPer1000Percentile(data[0].ev_per_1000_people_percentile ? Math.round(data[0].ev_per_1000_people_percentile) : null);
        } else {
          setEvPer1000Percentile(null);
        }
      } catch (error) {
        console.error('Error loading EV percentile:', error);
        setEvPer1000Percentile(null);
      }
    };

    loadEVPercentile();
  }, [selectedLocation, selectedGeofips]);

  // Fetch EV Charging percentile from ev_chargers table
  useEffect(() => {
    const loadEVChargingPercentile = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setEvChargingPercentile(null);
        return;
      }

      try {
        // Get the latest year's data (typically 2024)
        const { data, error } = await supabaseClient
          .from('ev_chargers')
          .select('year, chargers_per_1000_residents_percentile')
          .eq('geofips', selectedGeofips)
          .not('chargers_per_1000_residents_percentile', 'is', null)
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading EV Charging percentile:', error);
          setEvChargingPercentile(null);
          return;
        }

        if (data && data.length > 0) {
          setEvChargingPercentile(data[0].chargers_per_1000_residents_percentile ? Math.round(data[0].chargers_per_1000_residents_percentile) : null);
        } else {
          setEvChargingPercentile(null);
        }
      } catch (error) {
        console.error('Error loading EV Charging percentile:', error);
        setEvChargingPercentile(null);
      }
    };

    loadEVChargingPercentile();
  }, [selectedLocation, selectedGeofips]);

  // Fetch Solar PV per capita percentile from solar_pv_per_capita view
  useEffect(() => {
    const loadSolarPVPercentile = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setSolarPVPercentile(null);
        return;
      }

      try {
        // Get the latest year's data (typically 2023)
        const { data, error } = await supabaseClient
          .from('solar_pv_per_capita')
          .select('year, capacity_per_capita_percentile')
          .eq('geoid', selectedGeofips)
          .not('capacity_per_capita_percentile', 'is', null)
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading Solar PV percentile:', error);
          setSolarPVPercentile(null);
          return;
        }

        if (data && data.length > 0) {
          setSolarPVPercentile(data[0].capacity_per_capita_percentile ? Math.round(data[0].capacity_per_capita_percentile) : null);
        } else {
          setSolarPVPercentile(null);
        }
      } catch (error) {
        console.error('Error loading Solar PV percentile:', error);
        setSolarPVPercentile(null);
      }
    };

    loadSolarPVPercentile();
  }, [selectedLocation, selectedGeofips]);

  // Fetch Heat Pump per 10,000 residents percentile from heat_pump_per_capita view
  useEffect(() => {
    const loadHeatPumpPercentile = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setHeatPumpPercentile(null);
        return;
      }

      try {
        // Get the latest year's data
        const { data, error } = await supabaseClient
          .from('heat_pump_per_capita')
          .select('year, units_per_10000_percentile')
          .eq('geoid', selectedGeofips)
          .not('units_per_10000_percentile', 'is', null)
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading Heat Pump percentile:', error);
          setHeatPumpPercentile(null);
          return;
        }

        if (data && data.length > 0) {
          setHeatPumpPercentile(data[0].units_per_10000_percentile ? Math.round(data[0].units_per_10000_percentile) : null);
        } else {
          setHeatPumpPercentile(null);
        }
      } catch (error) {
        console.error('Error loading Heat Pump percentile:', error);
        setHeatPumpPercentile(null);
      }
    };

    loadHeatPumpPercentile();
  }, [selectedLocation, selectedGeofips]);

  // Fetch Educational Attainment percentile from educational_attainment_percentiles view
  useEffect(() => {
    const loadEducationalAttainmentPercentile = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setEducationalAttainmentPercentile(null);
        return;
      }

      try {
        // Get the latest year's data
        const { data, error } = await supabaseClient
          .from('educational_attainment_percentiles')
          .select('year, percent_bachelors_percentile')
          .eq('geofips', selectedGeofips)
          .not('percent_bachelors_percentile', 'is', null)
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading Educational Attainment percentile:', error);
          setEducationalAttainmentPercentile(null);
          return;
        }

        if (data && data.length > 0) {
          setEducationalAttainmentPercentile(data[0].percent_bachelors_percentile ? Math.round(data[0].percent_bachelors_percentile) : null);
        } else {
          setEducationalAttainmentPercentile(null);
        }
      } catch (error) {
        console.error('Error loading Educational Attainment percentile:', error);
        setEducationalAttainmentPercentile(null);
      }
    };

    loadEducationalAttainmentPercentile();
  }, [selectedLocation, selectedGeofips]);

  // Fetch Equity Indicators percentiles from equity_indicators_summary table
  useEffect(() => {
    const loadEquityPercentiles = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setEquityPercentiles(null);
        return;
      }

      try {
        // Get the latest year's data
        const { data, error } = await supabaseClient
          .from('equity_indicators_summary')
          .select('year, income_equity_percentile, education_equity_percentile, wage_equity_percentile')
          .eq('geofips', selectedGeofips)
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading Equity percentiles:', error);
          setEquityPercentiles(null);
          return;
        }

        if (data && data.length > 0) {
          const latest = data[0];
          // For equity indicators in Benefits section, invert the percentile
          // Higher ratio = worse equity = higher percentile in DB
          // But for Benefits, we want: Higher equity (lower ratio) = Higher percentile (better)
          // So: inverted_percentile = 100 - stored_percentile
          setEquityPercentiles({
            incomeEquity: latest.income_equity_percentile !== null && latest.income_equity_percentile !== undefined 
              ? Math.round(100 - latest.income_equity_percentile) 
              : undefined,
            educationEquity: latest.education_equity_percentile !== null && latest.education_equity_percentile !== undefined 
              ? Math.round(100 - latest.education_equity_percentile) 
              : undefined,
            wageEquity: latest.wage_equity_percentile !== null && latest.wage_equity_percentile !== undefined 
              ? Math.round(100 - latest.wage_equity_percentile) 
              : undefined,
          });
        } else {
          setEquityPercentiles(null);
        }
      } catch (error) {
        console.error('Error loading Equity percentiles:', error);
        setEquityPercentiles(null);
      }
    };

    loadEquityPercentiles();
  }, [selectedLocation, selectedGeofips]);

  // Fetch Health Insurance percentile from health_insurance_percentiles view
  useEffect(() => {
    const loadHealthInsurancePercentile = async () => {
      if (!selectedLocation || !selectedGeofips) {
        setHealthInsurancePercentile(null);
        return;
      }

      try {
        // Get the latest year's data
        const { data, error } = await supabaseClient
          .from('health_insurance_percentiles')
          .select('year, percent_with_insurance_percentile')
          .eq('geofips', selectedGeofips)
          .not('percent_with_insurance_percentile', 'is', null)
          .order('year', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error loading Health Insurance percentile:', error);
          setHealthInsurancePercentile(null);
          return;
        }

        if (data && data.length > 0) {
          setHealthInsurancePercentile(data[0].percent_with_insurance_percentile ? Math.round(data[0].percent_with_insurance_percentile) : null);
        } else {
          setHealthInsurancePercentile(null);
        }
      } catch (error) {
        console.error('Error loading Health Insurance percentile:', error);
        setHealthInsurancePercentile(null);
      }
    };

    loadHealthInsurancePercentile();
  }, [selectedLocation, selectedGeofips]);

  // Helper function to calculate average percentile from indicators
  // Returns 50 if no data exists for any subcategories
  const calculateCategoryPercentile = (indicators: { name: string; percentile: number | null }[]): number => {
    if (indicators.length === 0) return 50;
    const validIndicators = indicators.filter(ind => ind.percentile !== null);
    if (validIndicators.length === 0) return 50;
    const sum = validIndicators.reduce((acc, ind) => acc + (ind.percentile || 0), 0);
    return Math.round(sum / validIndicators.length);
  };

  // Helper function to get percentile for an indicator, using CES4 data if available
  const getIndicatorPercentile = (indicatorName: string, fallback: number): number => {
    if (!ces4Data) return fallback;
    
    const ces4Field = CES4_INDICATOR_MAP[indicatorName];
    if (ces4Field && ces4Data[ces4Field] !== null && ces4Data[ces4Field] !== undefined) {
      return Math.round(ces4Data[ces4Field] as number);
    }
    
    return fallback;
  };

  // Placeholder data structure - values span full range to show all color quintiles
  // Category percentiles will be calculated as averages of their indicators
  // CES4 data is used for Pollution and Health indicators when available
  const baseCategories: Omit<Category, 'percentile'>[] = type === 'burdens' ? [
    {
      name: 'Greenhouse Gas Emissions (per capita)',
      expanded: false,
      indicators: [
        { name: 'Light-Duty Vehicles (LDV)', percentile: ghgPercentiles?.ldv ?? 10 },
        { name: 'Heavy-Duty Vehicles (HDV)', percentile: ghgPercentiles?.hdv ?? 18 },
        { name: 'Electricity', percentile: ghgPercentiles?.electricity ?? 12 },
        { name: 'Natural Gas', percentile: ghgPercentiles?.naturalGas ?? 16 },
        { name: 'Waste', percentile: ghgPercentiles?.waste ?? 14 },
        { name: 'Offroad', percentile: ghgPercentiles?.offroad ?? 19 }
      ]
    },
    {
      name: 'Pollution',
      expanded: false,
      indicators: [
        { name: 'Diesel PM', percentile: getIndicatorPercentile('Diesel PM', 38) },
        { name: 'PM2.5', percentile: getIndicatorPercentile('PM2.5', 32) },
        { name: 'Ozone', percentile: getIndicatorPercentile('Ozone', 36) },
        { name: 'Toxic Releases', percentile: getIndicatorPercentile('Toxic Releases', 34) },
        { name: 'Hazardous Waste', percentile: getIndicatorPercentile('Hazardous Waste', 33) }
      ]
    },
    {
      name: 'Health',
      expanded: false,
      indicators: [
        { name: 'Asthma Rate', percentile: getIndicatorPercentile('Asthma Rate', 52) },
        { name: 'Cancer Fatalities', percentile: null }, // Coming soon
        { name: 'Cardiovascular Disease', percentile: getIndicatorPercentile('Cardiovascular Disease', 55) },
        { name: 'Low Birth Weight', percentile: getIndicatorPercentile('Low Birth Weight', 54) },
        { name: 'Adult Depression', percentile: null } // Coming soon
      ]
    },
    {
      name: 'Transportation',
      expanded: false,
      indicators: [
        { name: 'Commute Time', percentile: commuteTimePercentile ?? null },
        { name: 'Traffic', percentile: getIndicatorPercentile('Traffic Density', 72) }
      ]
    },
    {
      name: 'Built Environment',
      expanded: false,
      indicators: [
        { name: 'Housing Burden', percentile: housingBurdenPercentile !== null && housingBurdenPercentile !== undefined ? Math.round(housingBurdenPercentile) : 88 },
        { name: 'Energy Cost Burden', percentile: null }, // Coming soon
        { name: 'Percent Fossil Heating', percentile: null }, // Coming soon
        { name: 'Urban Heat Island', percentile: null }, // Coming soon
        { name: 'Displacement Risk', percentile: null }, // Coming soon
        { name: 'Crime and Safety Risks', percentile: null } // Coming soon
      ]
    }
  ] : [
    {
      name: 'Equity',
      expanded: false,
      indicators: [
        { name: 'Income Equity by Race', percentile: equityPercentiles?.incomeEquity ?? null },
        { name: 'Educational Attainment Equity by Race', percentile: equityPercentiles?.educationEquity ?? null },
        { name: 'Wage Equity by Sector', percentile: equityPercentiles?.wageEquity ?? null }
      ]
    },
    {
      name: 'Socioeconomic',
      expanded: false,
      indicators: [
        { name: 'Median Household Income', percentile: null }, // Coming soon
        { name: 'Employment Rate', percentile: null }, // Coming soon
        { name: 'Educational Attainment', percentile: educationalAttainmentPercentile ?? null },
        { name: 'Health Insurance Coverage', percentile: healthInsurancePercentile ?? null },
        { name: 'Housing Affordability', percentile: null }, // Coming soon
        { name: 'Job Wages', percentile: null } // Coming soon
      ]
    },
    {
      name: 'Mobility',
      expanded: false,
      indicators: [
        { name: 'Biking Infrastructure', percentile: null }, // Coming soon
        { name: 'Public Transit', percentile: null }, // Coming soon
        { name: "EV's per 1000 People", percentile: evPer1000Percentile ?? null },
        { name: 'EV Charging', percentile: evChargingPercentile ?? null },
        { name: 'Walkscore', percentile: null } // Coming soon
      ]
    },
    {
      name: 'Built Environment',
      expanded: false,
      indicators: [
        { name: 'Affordable Housing', percentile: null }, // Coming soon
        { name: 'Solar PV', percentile: solarPVPercentile ?? null },
        { name: 'Heat Pump Adoption', percentile: heatPumpPercentile ?? null }
      ]
    },
    {
      name: 'Community Resources',
      expanded: false,
      indicators: [
        { name: 'Tree Canopy Coverage', percentile: null }, // Coming soon
        { name: 'Open Space Access', percentile: null }, // Coming soon
        { name: 'Public Safety', percentile: null } // Coming soon
      ]
    }
  ];

  // Calculate category percentiles as averages of their indicators
  const categories: Category[] = baseCategories.map(cat => ({
    ...cat,
    percentile: calculateCategoryPercentile(cat.indicators)
  }));

  // Calculate overall percentile as average of all categories and notify parent
  useEffect(() => {
    if (onPercentileCalculated && categories.length > 0) {
      const overallPercentile = Math.round(
        categories.reduce((sum, cat) => sum + cat.percentile, 0) / categories.length
      );
      onPercentileCalculated(overallPercentile);
    }
  }, [categories, onPercentileCalculated]);

  const handleCategoryClick = (categoryName: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  const handleExpandAll = () => {
    setExpandedCategories(new Set(categories.map(cat => cat.name)));
  };

  const handleCollapseAll = () => {
    setExpandedCategories(new Set());
  };

  // Map indicator names to their corresponding routes
  const getIndicatorRoute = (indicatorName: string): string | null => {
    const routeMap: { [key: string]: string } = {
      // Burdens - Transportation
      'Light-Duty Vehicles (LDV)': '/transportation',
      'Heavy-Duty Vehicles (HDV)': '/transportation',
      'Commute Time': '/transportation',
      'Traffic': '/transportation',
      // Burdens - Buildings/Energy
      'Electricity': '/buildings',
      'Natural Gas': '/buildings',
      'Energy Cost Burden': '/buildings',
      'Percent Fossil Heating': '/buildings',
      // Burdens - GHG
      'Waste': '/other',
      'Offroad': '/ghg',
      // Burdens - Built Environment
      'Housing Burden': '/people',
      // Burdens - Health
      'Asthma Rate': '/people',
      'Cardiovascular Disease': '/people',
      'Low Birth Weight': '/people',
      // Benefits - Transportation/Mobility
      "EV's per 1000 People": '/transportation',
      'EV Charging': '/transportation',
      'Public Transit': '/transportation',
      'Biking Infrastructure': '/transportation',
      // Benefits - Buildings
      'Solar PV': '/buildings',
      'Heat Pump Adoption': '/buildings',
      // Benefits - People/Socioeconomic
      'Income Equity by Race': '/people',
      'Educational Attainment Equity by Race': '/people',
      'Wage Equity by Sector': '/people',
      'Median Household Income': '/people',
      'Employment Rate': '/people',
      'Educational Attainment': '/people',
      'Health Insurance Coverage': '/people',
      'Housing Affordability': '/people',
      'Job Wages': '/people',
    };
    return routeMap[indicatorName] || null;
  };

  const handleIndicatorClick = (indicatorName: string) => {
    const route = getIndicatorRoute(indicatorName);
    if (route) {
      navigate(route);
      // Scroll to top after navigation
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    }
  };

  const getColor = (percentile: number) => {
    if (type === 'burdens') {
      // Burdens: Green (low) → Yellow → Red (high) - quintiles
      // Lower percentile = better (green), Higher percentile = worse (red)
      if (percentile < 20) return '#10b981';      // Green (0-20)
      if (percentile < 40) return '#34d399';     // Light Green (20-40)
      if (percentile < 60) return '#fbbf24';     // Yellow (40-60)
      if (percentile < 80) return '#f59e0b';     // Orange (60-80)
      return '#dc2626';                           // Red (80-100)
    } else {
      // Benefits: Red (low) → Yellow → Green (high) - quintiles
      // Lower percentile = worse (red), Higher percentile = better (green)
      if (percentile < 20) return '#dc2626';      // Red (0-20)
      if (percentile < 40) return '#f59e0b';      // Orange (20-40)
      if (percentile < 60) return '#fbbf24';     // Yellow (40-60)
      if (percentile < 80) return '#34d399';     // Light Green (60-80)
      return '#10b981';                           // Green (80-100)
    }
  };

  // Calculate total score as average of all category scores
  const totalScore = Math.round(
    categories.reduce((sum, cat) => sum + cat.percentile, 0) / categories.length
  );

  // Get label for percentile range
  const getLabel = (percentile: number) => {
    const rounded = Math.round(percentile);
    if (rounded < 20) return 'Very Low';
    if (rounded < 40) return 'Low';
    if (rounded >= 40 && rounded < 60) {
      // For average range, show "Less than Average" or "More than Average"
      // unless it's exactly 50% (rounded), which is just "Average"
      if (rounded === 50) return 'Average';
      return rounded < 50 ? 'Less than Average' : 'More than Average';
    }
    if (rounded < 80) return 'High';
    return 'Very High';
  };

  return (
    <div style={{ width: '100%', padding: '0' }}>
      {/* Total Score - Full Width */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>Total Score:</span>
          <span style={{ fontSize: '14px', color: '#374151', fontWeight: '600' }}>
            {totalScore}% ({getLabel(totalScore)})
          </span>
        </div>
        <div style={{ width: '100%', height: '24px', backgroundColor: '#e5e7eb', borderRadius: '4px', position: 'relative', overflow: 'hidden' }}>
          <div
            style={{
              width: `${totalScore}%`,
              height: '100%',
              backgroundColor: getColor(totalScore),
              transition: 'width 0.3s ease'
            }}
          />
          <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '11px', fontWeight: '600', color: '#374151' }}>
            {totalScore}%
          </span>
        </div>
        {/* Quintile Labels */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '12px' }}>
          <span style={{ color: type === 'burdens' ? '#10b981' : '#dc2626', fontWeight: '500' }}>Very Low</span>
          <span style={{ color: type === 'burdens' ? '#34d399' : '#f59e0b', fontWeight: '500' }}>Low</span>
          <span style={{ color: '#fbbf24', fontWeight: '500' }}>Average</span>
          <span style={{ color: type === 'burdens' ? '#f59e0b' : '#34d399', fontWeight: '500' }}>High</span>
          <span style={{ color: type === 'burdens' ? '#dc2626' : '#10b981', fontWeight: '500' }}>Very High</span>
        </div>
      </div>
      
      {/* Expand/Collapse Buttons */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '20px' }}>
        <button
          onClick={handleExpandAll}
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: '500',
            color: '#3b82f6',
            backgroundColor: 'white',
            border: '1px solid #3b82f6',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#eff6ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
        >
          Expand
        </button>
        <button
          onClick={handleCollapseAll}
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: '500',
            color: '#6b7280',
            backgroundColor: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
        >
          Collapse
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {categories.map((category) => {
          const isExpanded = expandedCategories.has(category.name);
          return (
            <div key={category.name} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px' }}>
              <div
                onClick={() => handleCategoryClick(category.name)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '8px',
                  backgroundColor: isExpanded ? '#f3f4f6' : 'transparent',
                  borderRadius: '4px'
                }}
              >
                <span style={{ fontWeight: '600', fontSize: '14px' }}>{category.name}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '200px', height: '24px', backgroundColor: '#e5e7eb', borderRadius: '4px', position: 'relative', overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${category.percentile}%`,
                        height: '100%',
                        backgroundColor: getColor(category.percentile),
                        transition: 'width 0.3s ease'
                      }}
                    />
                    <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '11px', fontWeight: '600', color: '#374151' }}>
                      {category.percentile}%
                    </span>
                  </div>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                    {isExpanded ? '▼' : '▶'}
                  </span>
                </div>
              </div>
              {isExpanded && (
                <div style={{ marginTop: '12px', paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {category.indicators.map((indicator) => {
                      const hasData = indicator.percentile !== null;
                      const percentile = indicator.percentile as number | null;
                      const route = getIndicatorRoute(indicator.name);
                      const isClickable = route !== null;
                      return (
                        <div key={indicator.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px' }}>
                          <span 
                            onClick={() => isClickable && handleIndicatorClick(indicator.name)}
                            style={{ 
                              fontSize: '12px', 
                              color: isClickable ? '#2563eb' : '#6b7280',
                              cursor: isClickable ? 'pointer' : 'default',
                              textDecoration: isClickable ? 'underline' : 'none',
                              textDecorationColor: isClickable ? '#2563eb' : 'transparent',
                              textUnderlineOffset: '2px'
                            }}
                            title={isClickable ? `Click to view ${indicator.name} chart` : undefined}
                          >
                            {indicator.name}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {hasData && percentile !== null ? (
                              <div style={{ width: '150px', height: '20px', backgroundColor: '#e5e7eb', borderRadius: '4px', position: 'relative', overflow: 'hidden' }}>
                                <div
                                  style={{
                                    width: `${percentile}%`,
                                    height: '100%',
                                    backgroundColor: getColor(percentile),
                                    transition: 'width 0.3s ease'
                                  }}
                                />
                                <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '10px', fontWeight: '600', color: '#374151' }}>
                                  {percentile}%
                                </span>
                              </div>
                            ) : (
                              <span style={{ fontSize: '10px', color: '#9ca3af', fontStyle: 'italic' }}>
                                Coming soon
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
